import supabase, { 
  ENVIRONMENT, 
  createSupabaseClient, 
  getCurrentEnvironment 
} from './supabaseClient';
import * as Sentry from '@sentry/react';

/**
 * Data Access Layer for Supabase
 * Handles database operations and environment switching
 */
class SupabaseDAL {
  constructor() {
    this._client = supabase;
    this._currentEnvironment = getCurrentEnvironment();
  }

  /**
   * Get the current client instance
   */
  get client() {
    return this._client;
  }

  /**
   * Get the current environment
   */
  get currentEnvironment() {
    return this._currentEnvironment;
  }

  /**
   * Switch to a different environment
   * @param {string} environment - Environment to switch to (from ENVIRONMENT enum)
   * @returns {boolean} Success status
   */
  switchEnvironment(environment) {
    if (!Object.values(ENVIRONMENT).includes(environment)) {
      console.error('Invalid environment:', environment);
      
      // Track invalid environment selection in Sentry
      if (Sentry && Sentry.captureMessage) {
        Sentry.captureMessage('Invalid environment selection attempt', {
          level: 'warning',
          contexts: {
            environment: {
              current: this._currentEnvironment,
              attempted: environment
            }
          }
        });
      }
      
      return false;
    }

    try {
      this._client = createSupabaseClient(environment);
      this._currentEnvironment = environment;
      return true;
    } catch (error) {
      console.error('Error switching environments:', error);
      
      // Track environment switch failure in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.switchEnvironment", {
          fromEnvironment: this._currentEnvironment,
          toEnvironment: environment
        });
        Sentry.captureException(error);
      }
      
      return false;
    }
  }

  /**
   * Test the database connection
   * @returns {Promise<{success: boolean, error: string|null, data: any}>} Test result
   */
  async testConnection() {
    try {
      // Test connection using the actual table
      const { data, error } = await this._client
        .from('tblblasterhighscore')
        .select('count')
        .limit(1);

      if (error) {
        throw error;
      }

      return {
        success: true,
        error: null,
        data: { message: 'Connection successful' }
      };
    } catch (error) {
      console.error('Database connection test failed:', error);
      
      // Track connection failure in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.testConnection", {
          environment: this._currentEnvironment,
          error: error.message || 'Unknown error'
        });
        Sentry.captureException(error);
      }
      
      return {
        success: false,
        error: error.message || 'Unknown error',
        data: null
      };
    }
  }

  /**
   * Generic query method to fetch data from any table
   * @param {string} tableName - The table to query
   * @param {Object} options - Query options
   * @returns {Promise<{data: any, error: any}>} Query result
   */
  async query(tableName, options = {}) {
    const {
      select = '*',
      filter = {},
      limit = 10,
      offset = 0,
      order = { column: 'created_at', ascending: false }
    } = options;

    try {
      let query = this._client
        .from(tableName)
        .select(select);

      // Apply filters
      Object.entries(filter).forEach(([column, value]) => {
        query = query.eq(column, value);
      });

      // Apply ordering
      query = query.order(order.column, { ascending: order.ascending });

      // Apply pagination using range
      query = query.range(offset, offset + limit - 1);

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error(`Error querying table ${tableName}:`, error);
      
      // Track query errors in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.query", {
          table: tableName,
          options: JSON.stringify(options),
          environment: this._currentEnvironment
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  }

  /**
   * Insert data into a table
   * @param {string} tableName - The table to insert into
   * @param {Object|Array} data - Data to insert
   * @returns {Promise<{data: any, error: any}>} Insert result
   */
  async insert(tableName, data) {
    try {
      const { data: result, error } = await this._client
        .from(tableName)
        .insert(data)
        .select();

      if (error) {
        // Track insert errors in Sentry
        if (Sentry && Sentry.captureException) {
          Sentry.setContext("DAL.insert", {
            table: tableName,
            dataType: Array.isArray(data) ? 'array' : 'object',
            recordCount: Array.isArray(data) ? data.length : 1,
            environment: this._currentEnvironment,
            error: error.message
          });
          Sentry.captureException(error);
        }
      }

      return { data: result, error };
    } catch (error) {
      console.error(`Error inserting into table ${tableName}:`, error);
      
      // Track severe insert errors in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.insert", {
          table: tableName,
          dataType: Array.isArray(data) ? 'array' : 'object',
          recordCount: Array.isArray(data) ? data.length : 1,
          environment: this._currentEnvironment
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  }

  /**
   * Update data in a table
   * @param {string} tableName - The table to update
   * @param {Object} data - Data to update
   * @param {Object} match - Conditions to match rows for update
   * @returns {Promise<{data: any, error: any}>} Update result
   */
  async update(tableName, data, match) {
    try {
      let query = this._client.from(tableName).update(data);

      // Apply match conditions
      Object.entries(match).forEach(([column, value]) => {
        query = query.eq(column, value);
      });

      const { data: result, error } = await query.select();

      if (error) {
        // Track update errors in Sentry
        if (Sentry && Sentry.captureException) {
          Sentry.setContext("DAL.update", {
            table: tableName,
            matchConditions: JSON.stringify(match),
            environment: this._currentEnvironment,
            error: error.message
          });
          Sentry.captureException(error);
        }
      }

      return { data: result, error };
    } catch (error) {
      console.error(`Error updating table ${tableName}:`, error);
      
      // Track severe update errors in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.update", {
          table: tableName,
          matchConditions: JSON.stringify(match),
          environment: this._currentEnvironment
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  }

  /**
   * Delete data from a table
   * @param {string} tableName - The table to delete from
   * @param {Object} match - Conditions to match rows for deletion
   * @returns {Promise<{data: any, error: any}>} Delete result
   */
  async delete(tableName, match) {
    try {
      let query = this._client.from(tableName).delete();

      // Apply match conditions
      Object.entries(match).forEach(([column, value]) => {
        query = query.eq(column, value);
      });

      const { data, error } = await query.select();

      if (error) {
        // Track delete errors in Sentry
        if (Sentry && Sentry.captureException) {
          Sentry.setContext("DAL.delete", {
            table: tableName,
            matchConditions: JSON.stringify(match),
            environment: this._currentEnvironment,
            error: error.message
          });
          Sentry.captureException(error);
        }
      }

      return { data, error };
    } catch (error) {
      console.error(`Error deleting from table ${tableName}:`, error);
      
      // Track severe delete errors in Sentry
      if (Sentry && Sentry.captureException) {
        Sentry.setContext("DAL.delete", {
          table: tableName,
          matchConditions: JSON.stringify(match),
          environment: this._currentEnvironment
        });
        Sentry.captureException(error);
      }
      
      return { data: null, error };
    }
  }
}

// Create a singleton instance
const supabaseDAL = new SupabaseDAL();

export default supabaseDAL; 